import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { ActiveOffer } from '../lib/types';

interface UseActiveOffersResult {
  offers: ActiveOffer[];
  loading: boolean;
  error: string | null;
  assignOffer: (offerId: string, driverId: string) => Promise<boolean>;
}

export function useActiveOffers(): UseActiveOffersResult {
  const [offers, setOffers] = useState<ActiveOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchOffers() {
      setLoading(true);
      const { data, error: err } = await supabase
        .from('active_offers')
        .select('*, assigned_driver:drivers(id, full_name)')
        .eq('status', 'open')
        .order('created_at', { ascending: false });

      if (err) {
        setError(err.message);
      } else {
        setOffers((data as ActiveOffer[]) ?? []);
      }
      setLoading(false);
    }

    fetchOffers();

    // Real-time subscription — updates offers list live
    const channel = supabase
      .channel('active-offers-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'active_offers' },
        () => fetchOffers()
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const assignOffer = async (offerId: string, driverId: string): Promise<boolean> => {
    const { error: err } = await supabase
      .from('active_offers')
      .update({ status: 'assigned', assigned_driver_id: driverId })
      .eq('id', offerId);

    return !err;
  };

  return { offers, loading, error, assignOffer };
}
